// Cron page — registered arq cron jobs + their last execution status (PX-98)
// + runtime enable/disable toggle (PX-102, gated cron.write). Out of scope:
// manual run, drill-down history (separate PRs).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { APIError, request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { Toggle } from '@/components/FormControls'
import { PERM, type CronJobStatus, type CronListResponse, type CronRunResponse } from '@/types/api'

function pad(n: number | null): string {
  if (n === null) return '*'
  return n.toString().padStart(2, '0')
}

function formatSchedule(item: CronJobStatus): string {
  return `${pad(item.schedule_hour)}:${pad(item.schedule_minute)} UTC`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '—'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60_000).toFixed(1)}m`
}

/**
 * Per-cron mapping: did the last run actually change anything?
 *
 * - `sync_recent_fal_models`: new rows inserted = `upserted > 0`.
 * - `cleanup_deleted_users`: hard-deleted some users = `deleted > 0`.
 * - Unknown cron: fall back to "did the summary have any numeric > 0".
 */
function hadChanges(item: CronJobStatus): boolean {
  if (item.latest_status !== 'success' || !item.latest_summary) return false
  const s = item.latest_summary as Record<string, unknown>
  switch (item.name) {
    case 'sync_recent_fal_models':
      return typeof s.upserted === 'number' && s.upserted > 0
    case 'cleanup_deleted_users':
      return typeof s.deleted === 'number' && s.deleted > 0
    default:
      return Object.values(s).some((v) => typeof v === 'number' && v > 0)
  }
}

function StatusBadge({ status }: { status: CronJobStatus['latest_status'] }) {
  if (status === null) {
    return (
      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-500">
        never run
      </span>
    )
  }
  const color =
    status === 'success' ? 'bg-emerald-100 text-emerald-700' :
    status === 'error' ? 'bg-red-100 text-red-700' :
    'bg-yellow-100 text-yellow-700'
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${color}`}>
      {status}
    </span>
  )
}

export function CronPage() {
  const { profile } = useAuth()
  const canManage = hasPerm(profile, PERM.cron_write)
  const queryClient = useQueryClient()

  const cronQuery = useQuery({
    queryKey: ['admin', 'cron'],
    queryFn: () => request<CronListResponse>('/admin/cron'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

  const toggle = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      request<CronJobStatus>(`/admin/cron/${name}`, { method: 'PATCH', body: { enabled } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'cron'] }),
  })

  // PX-103: enqueue a cron to run immediately. Bypasses the enable/disable
  // toggle server-side (an explicit operator override). The cron runs async in
  // the worker, so we just invalidate and let the list re-fetch the new run.
  const runNow = useMutation({
    mutationFn: ({ name }: { name: string }) =>
      request<CronRunResponse>(`/admin/cron/${name}/run`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'cron'] }),
  })

  const items = cronQuery.data?.items ?? []

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Cron jobs</h1>
        <p className="text-sm text-ink-400">
          Daily scheduled tasks running in the arq worker. Source of truth:{' '}
          <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">
            WorkerSettings.cron_jobs
          </code>{' '}
          in <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">
            app/workers/generation_worker.py
          </code>.
        </p>
      </header>

      {cronQuery.isLoading && (
        <div className="text-sm text-ink-400">Loading…</div>
      )}

      {(() => {
        const err =
          (runNow.error instanceof APIError && runNow.error) ||
          (toggle.error instanceof APIError && toggle.error)
        return err ? (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {err.message}
          </div>
        ) : null
      })()}
      {!canManage && (
        <p className="mb-3 text-xs text-ink-400">Read-only — managing crons (toggle / run now) requires the “cron.write” permission.</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Enabled</th>
              <th className="px-4 py-3">Run</th>
              <th className="px-4 py-3">Schedule</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Changes</th>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Summary</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.name} className="border-b border-ink-50 last:border-b-0 align-top">
                <td className="px-4 py-3">
                  <Link
                    to={`/cron/${encodeURIComponent(item.name)}`}
                    className="font-mono text-ink-900 hover:text-brand-700 hover:underline"
                  >
                    {item.name}
                  </Link>
                  <div className="mt-1 text-xs text-ink-400">registered · history →</div>
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={item.enabled}
                    disabled={!canManage || (toggle.isPending && toggle.variables?.name === item.name)}
                    onChange={(v) => toggle.mutate({ name: item.name, enabled: v })}
                  />
                  {!item.enabled && (
                    <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-red-600">disabled</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => runNow.mutate({ name: item.name })}
                    disabled={!canManage || (runNow.isPending && runNow.variables?.name === item.name)}
                    className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title={canManage ? 'Enqueue this cron to run immediately (bypasses the on/off toggle)' : 'Requires cron.write'}
                  >
                    {runNow.isPending && runNow.variables?.name === item.name ? 'Starting…' : 'Run now'}
                  </button>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-700">
                  {formatSchedule(item)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={item.latest_status} />
                </td>
                <td className="px-4 py-3">
                  {item.latest_status === null ? (
                    <span className="text-ink-400">—</span>
                  ) : hadChanges(item) ? (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                      yes
                    </span>
                  ) : (
                    <span className="text-ink-400">no</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-ink-700">
                  {formatDateTime(item.latest_started_at)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-700">
                  {formatDuration(item.latest_started_at, item.latest_finished_at)}
                </td>
                <td className="px-4 py-3">
                  {item.latest_status === 'error' && item.latest_error_message ? (
                    <pre className="max-w-md whitespace-pre-wrap text-xs text-red-700">
                      {item.latest_error_message}
                    </pre>
                  ) : item.latest_summary && Object.keys(item.latest_summary).length > 0 ? (
                    <pre className="max-w-md overflow-x-auto whitespace-pre-wrap text-xs text-ink-700">
                      {JSON.stringify(item.latest_summary, null, 2)}
                    </pre>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!cronQuery.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-ink-400">
                  No cron jobs registered.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-ink-400">
        Latest run rows come from the <code>cron_runs</code> table, written
        by <code>app.workers.cron_recorder.record_cron_run</code>. Crons that
        fire BEFORE the recorder was wired (or have never fired since
        deploy) show «never run».
      </p>
    </div>
  )
}
